import React, { useState } from 'react'
import { Card } from 'react-bootstrap'
import { useContentImage } from '../../hooks/useContentImage'
import PillsBBox from '../PillsBBox'
import { Content } from '../../types/models/content'
import { useAppSelector } from '../../store/hooks'
import {
  selectSelectedAnnotationIds,
  selectSelectedProjectId,
} from '../../store/slices/projectSlice'
import PillsClassification from '../PillsClassification'
import ContentImageCard from '../annotation/ContentImageCard'
import PillsDataset from '../PillsDataset'
import { useGetProjectQuery } from '../../services/projectsApi'
import styled from 'styled-components'

const ImageCard = styled(Card)<{ $isHovered: boolean; $isSelected: boolean }>`
  height: 100%;
  width: 100%;
  cursor: pointer;
  position: relative;
  transform: ${(props) => (props.$isHovered ? 'scale(1.02)' : 'scale(1)')};
  box-shadow: ${(props) =>
    props.$isSelected
      ? '0 0 15px rgba(220, 53, 69, 0.5)'
      : props.$isHovered
      ? '0 0 15px rgba(10, 209, 20, 0.5)'
      : 'none'};
  transition: all 0.3s ease-in-out;
  border: ${(props) => (props.$isSelected ? '2px solid #dc3545' : 'none')};
  background-color: #2c2c2c;
`

const LoadingContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #2c2c2c;
  color: #666;
  font-size: 0.9rem;
`

interface ContentImageProps {
  content: Content
  onImageClick: (image: Content) => void
  selectable: boolean
  showDatasource?: boolean
  selected?: boolean
}

const ContentImage: React.FC<ContentImageProps> = ({
  content,
  onImageClick,
  selectable = false,
  showDatasource = false,
  selected = false,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedAnnotationIds = useAppSelector(selectSelectedAnnotationIds)

  const { base64Image, imageRef, contentData, imageLoading } = useContentImage({
    contentId: content.id,
    projectId: selectedProjectId || '',
    datasetId: project?.datasetid || '',
  })

  const [isHovered, setIsHovered] = useState<boolean>(false)

  // Helper function to safely get bounding boxes
  const getBoundingBoxes = () => {
    if (!content.annotation || content.annotation.length === 0) return []
    if (!content.annotation[0].metadata) return []
    const boxes =
      project?.annotation_type === 'bounding_box'
        ? content.annotation[0].metadata.bounding_boxes || []
        : project?.annotation_type === 'oriented_bounding_box'
        ? content.annotation[0].metadata.oriented_bounding_boxes || []
        : []
    return boxes.filter((box) => box && box.name) // Filter out invalid boxes
  }

  // Helper function to safely get tag IDs for classification
  const getTagIds = () => {
    if (!content.annotation || content.annotation.length === 0) return []
    const tagIds = content.annotation[0].tagids || []
    return tagIds.filter((id) => id && id.trim() !== '') // Filter out empty/invalid IDs
  }

  // Check if content has valid annotations
  const hasValidAnnotations = () => {
    if (!content.annotation || content.annotation.length === 0) return false
    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      return getBoundingBoxes().length > 0
    }
    if (project?.annotation_type === 'classification') {
      return getTagIds().length > 0
    }
    return false
  }

  return (
    <div
      ref={imageRef}
      style={{ height: '100%', width: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onImageClick(content)}
    >
      <ImageCard bg="bg-dark" text="bg-dark" $isHovered={isHovered} $isSelected={selected}>
        {base64Image && contentData ? (
          <ContentImageCard
            base64Data={base64Image}
            bounding_boxes={getBoundingBoxes()}
            contentWidth={contentData.width}
            contentHeight={contentData.height}
          />
        ) : (
          <LoadingContainer>{imageLoading ? 'Loading...' : 'No image'}</LoadingContainer>
        )}
        {selectable && (
          <input
            type="checkbox"
            className="checkbox-top-right"
            checked={selectedAnnotationIds.includes(content.id)}
          />
        )}
        {showDatasource && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1,
            }}
          >
            <PillsDataset
              tiny={true}
              datasetNames={content?.metadata?.datasource ? [content?.metadata?.datasource] : []}
            />
          </div>
        )}
        {(project?.annotation_type === 'bounding_box' ||
          project?.annotation_type === 'oriented_bounding_box') &&
          hasValidAnnotations() && (
            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                zIndex: 1000,
              }}
            >
              <PillsBBox bounding_boxes={getBoundingBoxes()} showTagCounts={true} maxTags={3} />
            </div>
          )}
        {project?.annotation_type === 'classification' && hasValidAnnotations() && (
          <div
            style={{
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              zIndex: 1000,
            }}
          >
            <PillsClassification tagIds={getTagIds()} maxTags={3} />
          </div>
        )}
      </ImageCard>
    </div>
  )
}

export default ContentImage
